import React, { useCallback, useEffect, useState } from 'react';
import { adminAPI } from '../../api/api';
import {
  ScrollText, RefreshCw, ChevronLeft, ChevronRight, Download,
  UserX, UserCheck, Trash2, ShieldCheck, Scale, Wallet, Ban, Zap,
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const ACTION_META = {
  'user.suspend': { label: 'Suspended user', icon: UserX, color: 'text-red-600', bg: 'bg-red-50' },
  'user.unsuspend': { label: 'Unsuspended user', icon: UserCheck, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  'user.verify': { label: 'Verified user', icon: ShieldCheck, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  'user.unverify': { label: 'Unverified user', icon: ShieldCheck, color: 'text-neutral-600', bg: 'bg-neutral-50' },
  'user.delete': { label: 'Deleted user', icon: Trash2, color: 'text-red-600', bg: 'bg-red-50' },
  'job.delete': { label: 'Deleted job', icon: Trash2, color: 'text-red-600', bg: 'bg-red-50' },
  'review.delete': { label: 'Deleted review', icon: Trash2, color: 'text-red-600', bg: 'bg-red-50' },
  'escrow.resolve_dispute': { label: 'Resolved dispute', icon: Scale, color: 'text-[#D95800]', bg: 'bg-orange-50' },
  'escrow.force_release': { label: 'Force-released escrow', icon: Zap, color: 'text-[#D95800]', bg: 'bg-orange-50' },
  'escrow.cancel': { label: 'Cancelled escrow', icon: Ban, color: 'text-red-600', bg: 'bg-red-50' },
  'payout.approve': { label: 'Approved payout', icon: Wallet, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  'payout.reject': { label: 'Rejected payout', icon: Wallet, color: 'text-red-600', bg: 'bg-red-50' },
};

const fmtMeta = (m) => {
  if (!m || Object.keys(m).length === 0) return null;
  return Object.entries(m)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${typeof v === 'number' && /amount|refund/.test(k) ? `$${v}` : v}`)
    .join(' · ');
};

const AuditLogTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [actionKeys, setActionKeys] = useState([]);
  const [filter, setFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exporting, setExporting] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 30, action: filter };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await adminAPI.getAuditLogs(params);
      const d = res.data || {};
      setLogs(d.logs || []);
      setPages(d.pages || 1);
      setTotal(d.total || 0);
      if (d.action_keys) setActionKeys(d.action_keys);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [page, filter, dateFrom, dateTo]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filter && filter !== 'all') params.set('action', filter);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/audit-logs/export?${params.toString()}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      const href = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(href);
      toast({ title: 'Export ready', description: 'Your audit log CSV is downloading.' });
    } catch {
      toast({ title: 'Export failed', description: 'Could not generate the CSV. Try again.', variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : 'N/A');

  return (
    <div className="space-y-5" data-testid="audit-log-tab">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2 text-neutral-900 dark:text-white">
            <ScrollText className="h-6 w-6 text-[#D95800]" />
            Audit Log
          </h2>
          <p className="text-sm text-neutral-500 mt-0.5">
            Every privileged admin action — disputes, escrow releases, payouts, and user changes. {total} recorded.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5">
            <label className="text-xs font-medium text-neutral-500">From</label>
            <input
              type="date"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => { setPage(1); setDateFrom(e.target.value); }}
              className="h-10 px-2 border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 dark:text-white text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95800]"
              data-testid="audit-date-from"
            />
            <label className="text-xs font-medium text-neutral-500">To</label>
            <input
              type="date"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => { setPage(1); setDateTo(e.target.value); }}
              className="h-10 px-2 border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 dark:text-white text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95800]"
              data-testid="audit-date-to"
            />
            {(dateFrom || dateTo) && (
              <button
                onClick={() => { setPage(1); setDateFrom(''); setDateTo(''); }}
                className="h-10 px-2 text-xs font-medium text-neutral-500 hover:text-[#D95800]"
                data-testid="audit-date-clear"
                title="Clear dates"
              >
                Clear
              </button>
            )}
          </div>
          <select
            value={filter}
            onChange={(e) => { setPage(1); setFilter(e.target.value); }}
            className="h-10 px-3 border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 dark:text-white text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95800]"
            data-testid="audit-filter-select"
          >
            <option value="all">All actions</option>
            {actionKeys.map((k) => (
              <option key={k} value={k}>{ACTION_META[k]?.label || k}</option>
            ))}
          </select>
          <button
            onClick={exportCsv}
            disabled={exporting || total === 0}
            className="inline-flex items-center gap-2 h-10 px-3 border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 dark:text-white text-sm font-medium rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 disabled:opacity-40"
            data-testid="audit-export-btn"
            title="Export as CSV"
          >
            <Download className={`h-4 w-4 ${exporting ? 'animate-pulse' : ''}`} />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
          <button
            onClick={fetchLogs}
            className="h-10 w-10 flex items-center justify-center border border-neutral-300 dark:border-neutral-700 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800"
            data-testid="audit-refresh-btn"
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {loading && logs.length === 0 ? (
        <div className="text-center py-12 text-neutral-500 text-sm">Loading…</div>
      ) : logs.length === 0 ? (
        <div className="border border-dashed border-neutral-300 dark:border-neutral-700 rounded-lg p-12 text-center" data-testid="audit-empty">
          <ScrollText className="h-10 w-10 mx-auto text-neutral-400 mb-3" />
          <p className="font-semibold text-neutral-600 dark:text-neutral-300">No audit entries yet</p>
          <p className="text-sm text-neutral-500 mt-1">Privileged admin actions will appear here as they happen.</p>
        </div>
      ) : (
        <div className="border border-neutral-200 dark:border-neutral-800 rounded-lg divide-y divide-neutral-200 dark:divide-neutral-800 overflow-hidden">
          {logs.map((log) => {
            const meta = ACTION_META[log.action] || { label: log.action, icon: ScrollText, color: 'text-neutral-600', bg: 'bg-neutral-50' };
            const Icon = meta.icon;
            const details = fmtMeta(log.metadata);
            return (
              <div
                key={log._id}
                className="flex items-start gap-3 p-4 hover:bg-neutral-50 dark:hover:bg-neutral-900/50"
                data-testid={`audit-row-${log._id}`}
              >
                <div className={`flex-shrink-0 w-9 h-9 rounded-md flex items-center justify-center ${meta.bg} dark:bg-neutral-800`}>
                  <Icon className={`h-4 w-4 ${meta.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-semibold text-sm text-neutral-900 dark:text-white">{meta.label}</span>
                    {log.target_label && (
                      <span className="text-sm text-neutral-500 truncate">— {log.target_label}</span>
                    )}
                  </div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    by <span className="font-medium text-neutral-700 dark:text-neutral-300">{log.actor_name || 'Unknown admin'}</span>
                    {log.target_type && log.target_id && (
                      <> · {log.target_type} <code className="text-[10px]">{String(log.target_id).slice(-6)}</code></>
                    )}
                  </div>
                  {details && (
                    <p className="text-xs text-neutral-500 mt-1 break-words">{details}</p>
                  )}
                </div>
                <div className="flex-shrink-0 text-xs text-neutral-400 whitespace-nowrap">{fmtDate(log.created_at)}</div>
              </div>
            );
          })}
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="inline-flex items-center gap-1 h-9 px-3 border border-neutral-300 dark:border-neutral-700 rounded-md text-sm disabled:opacity-40 hover:bg-neutral-100 dark:hover:bg-neutral-800"
            data-testid="audit-prev-page"
          >
            <ChevronLeft className="h-4 w-4" /> Prev
          </button>
          <span className="text-sm text-neutral-500">Page {page} of {pages}</span>
          <button
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            disabled={page >= pages}
            className="inline-flex items-center gap-1 h-9 px-3 border border-neutral-300 dark:border-neutral-700 rounded-md text-sm disabled:opacity-40 hover:bg-neutral-100 dark:hover:bg-neutral-800"
            data-testid="audit-next-page"
          >
            Next <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default AuditLogTab;
